/**
 * A linkage schedule's cron, as the console reads it (Feature 77 D13).
 *
 * Five fields in UTC, as the agent reads them: an alias such as `@hourly`
 * and a six-field expression with seconds are refused there, so they are
 * here. A day of the month and a day of the week, when both are given, are
 * either one, as cron has always read them. The console lists the next
 * slots in UTC; reading a cron as words, in the viewer's timezone, is the
 * schedule model's (`schedule.ts`). The agent's own next slot stays the
 * truth for an organization.
 */

interface FieldRule {
  min: number;
  max: number;
  names?: readonly string[];
}

const MINUTE: FieldRule = { min: 0, max: 59 };
const HOUR: FieldRule = { min: 0, max: 23 };
const DAY: FieldRule = { min: 1, max: 31 };
const MONTH: FieldRule = {
  min: 1,
  max: 12,
  names: ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'],
};
// 7 is Sunday too; it folds into 0 once parsed.
const WEEKDAY: FieldRule = {
  min: 0,
  max: 7,
  names: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
};
const RULES = [MINUTE, HOUR, DAY, MONTH, WEEKDAY] as const;
// Slots are searched this far ahead before giving up, so `0 0 30 2 *` ends.
const SEARCH_MINUTES = 4 * 366 * 24 * 60;
const MINUTE_MS = 60_000;

/** One parsed field: the values it allows, and whether it was `*`. */
export interface CronField {
  values: ReadonlySet<number>;
  any: boolean;
}

/** A cron's five fields, parsed. */
export interface CronFields {
  minute: CronField;
  hour: CronField;
  day: CronField;
  month: CronField;
  weekday: CronField;
}

/** A field's single value, a number or one of its names; NaN when neither. */
function valueOf(text: string, rule: FieldRule): number {
  const named = rule.names?.indexOf(text.toUpperCase()) ?? -1;
  if (named >= 0) return named + (rule === MONTH ? 1 : 0);
  return /^\d+$/.test(text) ? Number(text) : Number.NaN;
}

/** The values one comma-separated part allows: `*`, `a`, `a-b`, each with an optional `/n`. */
function partValues(part: string, rule: FieldRule): number[] | null {
  const [range = '', stepText] = part.split('/');
  const step = stepText === undefined ? 1 : /^\d+$/.test(stepText) ? Number(stepText) : 0;
  if (step < 1 || part.split('/').length > 2) return null;
  let low = rule.min;
  let high = rule.max;
  if (range !== '*') {
    const [from = '', to] = range.split('-');
    low = valueOf(from, rule);
    // `a/n` runs from a to the field's end, as `a-max/n` would.
    high = to !== undefined ? valueOf(to, rule) : stepText !== undefined ? rule.max : low;
    if (range.split('-').length > 2) return null;
  }
  if (Number.isNaN(low) || Number.isNaN(high) || low < rule.min || high > rule.max || low > high) {
    return null;
  }
  const values: number[] = [];
  for (let v = low; v <= high; v += step) values.push(v);
  return values;
}

/** A field's values, or null when one of its parts is not read. */
function parseField(text: string, rule: FieldRule): CronField | null {
  const values = new Set<number>();
  for (const part of text.split(',')) {
    const found = partValues(part, rule);
    if (found === null) return null;
    for (const v of found) values.add(rule === WEEKDAY && v === 7 ? 0 : v);
  }
  return { values, any: text === '*' };
}

/** The five fields, or null when the cron is not five fields the agent reads. */
export function parseCron(cron: string): CronFields | null {
  const text = cron.trim();
  const parts = text.split(/\s+/);
  if (text.startsWith('@') || parts.length !== RULES.length) return null;
  const fields = parts.map((part, i) => parseField(part, RULES[i] as FieldRule));
  if (fields.some((f) => f === null)) return null;
  const [minute, hour, day, month, weekday] = fields as CronField[];
  if (!minute || !hour || !day || !month || !weekday) return null;
  return { minute, hour, day, month, weekday };
}

/** Whether `cron` is five UTC fields the agent accepts: no alias, no seconds. */
export function isValidCron(cron: string): boolean {
  return parseCron(cron) !== null;
}

/** Whether a day matches: either day field when both are given, else the one that is. */
function dayMatches(p: CronFields, at: Date): boolean {
  const byDate = p.day.values.has(at.getUTCDate());
  const byWeekday = p.weekday.values.has(at.getUTCDay());
  if (!p.day.any && !p.weekday.any) return byDate || byWeekday;
  return byDate && byWeekday;
}

/** The next `count` slots strictly after `after`, in UTC; fewer when the cron rarely fires. */
export function nextSlots(cron: string, after: Date, count: number): Date[] {
  const p = parseCron(cron);
  if (p === null) return [];
  const slots: Date[] = [];
  let at = new Date(Math.floor(after.getTime() / MINUTE_MS) * MINUTE_MS + MINUTE_MS);
  for (let step = 0; step < SEARCH_MINUTES && slots.length < count; step += 1) {
    if (!p.month.values.has(at.getUTCMonth() + 1)) {
      at = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth() + 1, 1));
    } else if (!dayMatches(p, at)) {
      at = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate() + 1));
    } else if (!p.hour.values.has(at.getUTCHours())) {
      at = new Date(
        Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate(), at.getUTCHours() + 1),
      );
    } else if (!p.minute.values.has(at.getUTCMinutes())) {
      at = new Date(at.getTime() + MINUTE_MS);
    } else {
      slots.push(at);
      at = new Date(at.getTime() + MINUTE_MS);
    }
  }
  return slots;
}

/** A slot in the viewer's timezone: `Mon 27 Oct 09:00`. */
export function slotText(at: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('weekday')} ${part('day')} ${part('month')} ${part('hour')}:${part('minute')}`;
}

/** The viewer's timezone, as the browser names it. */
export function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
